import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtRp  = n => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtDate = d => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  mono:  { fontFamily: "'DM Mono',monospace" },
  serif: { fontFamily: "'Baloo 2',sans-serif" },
  muted: { color: 'var(--muted)', fontSize: '.78rem' },
};

function makeReferralLink(ref_code) {
  return `${window.location.origin}/?ref=${encodeURIComponent(ref_code)}`;
}

// ─── Kartu per referral ───────────────────────────────────────────────────────
function ReferralCard({ item, rank }) {
  const [expanded, setExpanded] = useState(false);
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [copied, setCopied]     = useState(false);

  async function loadOrders() {
    if (orders.length > 0) { setExpanded(!expanded); return; }
    setLoading(true);
    const { data } = await supabase
      .from('po_orders')
      .select('id,qty,total,status,payment_method,notes,created_at,customer:customers(name,wa)')
      .eq('referred_by_user_id', item.user_id)
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
    setExpanded(true);
  }

  async function copyLink() {
    const link = makeReferralLink(item.ref_code);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      prompt('Salin link referral ini:', link);
    }
  }

  const rankColors = ['#F59E0B', '#9CA3AF', '#92400E'];
  const rankBgs    = ['#FFFBEB', '#F9FAFB', '#FEF3C7'];

  const STATUS_COLORS = {
    pending:   { bg: '#FFF3E0', c: '#E65100' },
    confirmed: { bg: '#E3F2FD', c: '#0D47A1' },
    paid:      { bg: '#E8F5E9', c: '#1B5E20' },
    batal:     { bg: '#FFEBEE', c: '#C62828' },
  };

  return (
    <div style={{ ...S.card, overflow: 'hidden', background: rank <= 3 ? rankBgs[rank - 1] : 'white' }}>
      {/* Header card */}
      <div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
        {/* Rank badge */}
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: rank <= 3 ? rankColors[rank - 1] : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: rank <= 3 ? '1rem' : '.8rem', color: rank <= 3 ? 'white' : '#6B7280', flexShrink: 0 }}>
          {rank <= 3 ? ['🥇','🥈','🥉'][rank - 1] : rank}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{item.user_name}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
            <span style={{ ...S.mono, fontSize: '.63rem', background: 'rgba(201,146,42,.1)', color: 'var(--brown)', borderRadius: 20, padding: '1px 8px' }}>
              ref: {item.ref_code}
            </span>
            {item.komisi_pct > 0 && (
              <span style={{ ...S.mono, fontSize: '.63rem', color: 'var(--muted)' }}>
                komisi {item.komisi_pct}%
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ ...S.serif, fontSize: '1.15rem', color: 'var(--brown)' }}>{fmtRp(item.total_omzet)}</div>
          <div style={{ ...S.mono, fontSize: '.62rem', color: 'var(--muted)' }}>{item.total_order} order · {item.total_pack} pack</div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ padding: '0 16px 10px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {item.komisi_pct > 0 && (
          <div style={{ background: '#FFF8E7', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 120 }}>
            <div style={{ ...S.mono, fontSize: '.6rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 2 }}>Komisi Terhitung</div>
            <div style={{ ...S.serif, fontSize: '1rem', color: 'var(--brown)' }}>{fmtRp(item.komisi_amount)}</div>
            <div style={{ fontSize: '.68rem', color: 'var(--muted)', marginTop: 1 }}>dari omzet COD + non-tunai lunas</div>
          </div>
        )}
        <div style={{ background: '#F0FBF4', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 120 }}>
          <div style={{ ...S.mono, fontSize: '.6rem', letterSpacing: '.8px', textTransform: 'uppercase', color: '#27AE60', marginBottom: 2 }}>Order Valid</div>
          <div style={{ ...S.serif, fontSize: '1rem', color: '#1B5E20' }}>{item.order_confirmed} order</div>
          <div style={{ fontSize: '.68rem', color: 'var(--muted)', marginTop: 1 }}>COD + non-tunai lunas</div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '0 16px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={copyLink} style={{ padding: '5px 12px', borderRadius: 6, background: copied ? '#E8F5E9' : '#EFF6FF', border: `1px solid ${copied ? '#A5D6A7' : '#BFDBFE'}`, color: copied ? '#1B5E20' : '#1C5BD4', fontSize: '.73rem', cursor: 'pointer', transition: 'all .2s' }}>
          {copied ? '✓ Tersalin' : '🔗 Salin Link'}
        </button>
        <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Halo! Order HAPin lewat link ini ya 👇\n' + makeReferralLink(item.ref_code))}`, '_blank')} style={{ padding: '5px 12px', borderRadius: 6, background: '#F0FBF4', border: '1px solid #A5D6A7', color: '#1B5E20', fontSize: '.73rem', cursor: 'pointer' }}>
          📤 Share WA
        </button>
        <button onClick={loadOrders} style={{ padding: '5px 12px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: '.73rem', cursor: 'pointer', marginLeft: 'auto' }}>
          {loading ? 'Memuat...' : expanded ? '▲ Sembunyikan' : `▼ Lihat ${item.total_order} Order`}
        </button>
      </div>

      {/* Detail order list */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', background: 'white' }}>
          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', ...S.muted }}>Belum ada order</div>
          ) : orders.map(o => {
            const sc = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
            return (
              <div key={o.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.82rem', fontWeight: 500 }}>{o.customer?.name || 'Anonim'}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                    {o.qty} pack · {fmtDate(o.created_at)}
                    {o.customer?.wa && (
                      <a href={`https://wa.me/62${o.customer.wa.replace(/^0/, '')}`} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: '#27AE60' }}>
                        WA
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ ...S.serif, fontSize: '.9rem' }}>{fmtRp(o.total)}</div>
                  <span style={{ ...S.mono, fontSize: '.6rem', background: sc.bg, color: sc.c, borderRadius: 20, padding: '1px 7px' }}>{o.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Referral() {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  async function load() {
    setLoading(true);

    // Ambil semua users yang punya ref_code
    const { data: users } = await supabase
      .from('users')
      .select('id,name,ref_code,referral_komisi_pct')
      .not('ref_code', 'is', null)
      .eq('is_active', true);

    if (!users || users.length === 0) { setData([]); setLoading(false); return; }

    // Ambil po_orders bereferral
    let q = supabase
      .from('po_orders')
      .select('qty,total,status,payment_method,payment_status,referred_by_user_id')
      .not('referred_by_user_id', 'is', null)
      .neq('status', 'batal');

    if (dateFrom) q = q.gte('created_at', dateFrom + 'T00:00:00');
    if (dateTo)   q = q.lte('created_at', dateTo   + 'T23:59:59');

    const { data: orders } = await q;

    // Agregat per user
    const result = users.map(u => {
      const myOrders = (orders || []).filter(o => o.referred_by_user_id === u.id);
      // Komisi hanya dari COD + non-tunai yang payment_status = 'paid'
      const confirmed = myOrders.filter(o =>
        o.payment_method === 'cod' ||
        (o.payment_method === 'non-tunai' && o.payment_status === 'paid')
      );
      const total_omzet = confirmed.reduce((s, o) => s + (o.total || 0), 0);
      const komisi_amount = Math.round(total_omzet * (u.referral_komisi_pct || 0) / 100);
      return {
        user_id:        u.id,
        user_name:      u.name,
        ref_code:       u.ref_code,
        komisi_pct:     u.referral_komisi_pct || 0,
        total_order:    myOrders.length,
        total_pack:     myOrders.reduce((s, o) => s + (o.qty || 0), 0),
        total_omzet,
        order_confirmed: confirmed.length,
        komisi_amount,
      };
    });

    // Sort by omzet desc
    result.sort((a, b) => b.total_omzet - a.total_omzet);
    setData(result);
    setLoading(false);
  }

  const grandOmzet   = data.reduce((s, d) => s + d.total_omzet, 0);
  const grandKomisi  = data.reduce((s, d) => s + d.komisi_amount, 0);
  const grandOrder   = data.reduce((s, d) => s + d.total_order, 0);

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ ...S.serif, fontSize: '1.5rem' }}>Referral</div>
        <div style={{ ...S.muted }}>Omzet & komisi per kasir / reseller</div>
      </div>

      {/* Filter tanggal */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '.74rem', color: 'var(--muted)' }}>Dari</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '.82rem', outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '.74rem', color: 'var(--muted)' }}>s/d</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '.82rem', outline: 'none' }} />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '7px 12px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.76rem', cursor: 'pointer', color: 'var(--muted)' }}>
            Reset
          </button>
        )}
      </div>

      {/* KPI global */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Total Omzet Referral', val: fmtRp(grandOmzet),  bg: 'white' },
          { label: 'Total Komisi',         val: fmtRp(grandKomisi), bg: '#FFF8E7' },
          { label: 'Total Order',          val: grandOrder + ' order', bg: 'white' },
          { label: 'Reseller Aktif',       val: data.filter(d => d.total_order > 0).length + ' orang', bg: 'white' },
        ].map(({ label, val, bg }) => (
          <div key={label} style={{ ...S.card, padding: '13px 15px', background: bg }}>
            <div style={{ ...S.mono, fontSize: '.6rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ ...S.serif, fontSize: '1.1rem' }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Daftar */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>Memuat...</div>
      ) : data.length === 0 ? (
        <div style={{ ...S.card, padding: 32, textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem' }}>
          Belum ada pengguna dengan kode referral.<br />
          <span style={{ fontSize: '.76rem' }}>Tambah kode referral di halaman <strong>Pengguna POS</strong>.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.map((item, i) => (
            <ReferralCard key={item.user_id} item={item} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
